/**
 * OnboardingThanks
 *
 * Brief thank-you screen rendered at /setup/thanks right after the YAML
 * onboarding submit succeeds. Replaces the immediate redirect to /api-keys
 * with a friendlier survey-style closer:
 *   - "Thanks for taking part!"
 *   - 3 quick-link cards (API-Key / PDF templates / Usage), with the API-key
 *     step leading the row — it's the actual next step, the other two are
 *     optional detours
 *   - Primary CTA "Get started" → /api-keys
 *
 * On CTA click we also set the WelcomeBanner's dismissal flag so the user
 * doesn't see the same set of quick-links twice on /api-keys.
 */

import { useAuth } from '@xhubio-saas/core-frontend/kernel'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@xhubio-saas/core-frontend/ui-components'
import { ArrowRight, BarChart3, FileText, Key, Sparkles } from 'lucide-react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { QuickLinkGrid, type QuickLinkItem } from './QuickLinkGrid'

const WELCOME_BANNER_DISMISS_KEY_PREFIX = 'welcome-banner-dismissed:'

function welcomeBannerDismissKey(userId: string | undefined): string {
  return `${WELCOME_BANNER_DISMISS_KEY_PREFIX}${userId ?? 'anonymous'}`
}

export function OnboardingThanks() {
  const { t } = useTranslation()
  const { session } = useAuth()
  const userId = session?.user?.id

  const handleStart = useCallback(() => {
    if (typeof window !== 'undefined') {
      // Suppress the WelcomeBanner on /api-keys — its quick-links would
      // duplicate what the user just saw on this screen. The rich empty-
      // state of frontend-plugin-api-key remains visible.
      localStorage.setItem(welcomeBannerDismissKey(userId), 'true')
      window.location.assign('/api-keys')
    }
  }, [userId])

  const links: QuickLinkItem[] = [
    {
      href: '/api-keys',
      icon: Key,
      title: t('onboarding.thanks.links.apiKeys.title'),
      description: t('onboarding.thanks.links.apiKeys.description'),
    },
    {
      href: '/pdf/v2/templates',
      icon: FileText,
      title: t('onboarding.thanks.links.templates.title'),
      description: t('onboarding.thanks.links.templates.description'),
    },
    {
      href: '/usage',
      icon: BarChart3,
      title: t('onboarding.thanks.links.usage.title'),
      description: t('onboarding.thanks.links.usage.description'),
    },
  ]

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10">
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="flex flex-row items-start gap-4 pb-2">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Sparkles className="h-6 w-6" />
          </div>
          <div>
            <CardTitle className="text-xl">{t('onboarding.thanks.title')}</CardTitle>
            <CardDescription className="mt-1">{t('onboarding.thanks.description')}</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <QuickLinkGrid links={links} layout="lead" />
          <div className="mt-6 flex justify-end">
            <Button onClick={handleStart} className="gap-2">
              {t('onboarding.thanks.cta')}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
